import React, { FC, HTMLProps, useEffect, useMemo, useState } from "react";
import cn from "classnames";
import stl from "./index.module.scss";
import { ActivityAvailability, PackageAvailability } from "../../store/models";
import isSameDay from "date-fns/isSameDay";
import isSameMinute from "date-fns/isSameMinute";
import { useStore } from "effector-react";
import { $currentLocale } from "../../store/shared/locales";
import { DayPeriodNames, detectDayPeriod, sortDataByDateByDay } from "./utils";
import { Tabs } from "../../components";

type Value = PackageAvailability | ActivityAvailability;
export type AvailabilityValue = Value[];
interface Props
  extends Omit<HTMLProps<HTMLDivElement>, "data" | "onSelect" | "selected"> {
  data: Value[];
  selected?: Value | null;
  onSelect: (v: Value) => void;
}
const AvailabilityCard: FC<Props> = ({
  data,
  selected,
  onSelect,
  ...props
}) => {
  const { dateFnsLocale: locale } = useStore($currentLocale);

  const [currentDayPeriod, setCurrentDayPeriod] = useState<DayPeriodNames>(
    DayPeriodNames.morning
  );
  const [currentDateIdx, setCurrentDateIdx] = useState<number>(0);

  const dataSortedByDateByDayPeriod = useMemo(() => {
    return sortDataByDateByDay(data, locale);
  }, [data, locale]);

  useEffect(() => {
    if (selected) {
      const dateIdx = dataSortedByDateByDayPeriod.findIndex(({ date }) => {
        return isSameDay(date, selected.date);
      });
      if (~dateIdx) setCurrentDateIdx(dateIdx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSortedByDateByDayPeriod]);

  useEffect(() => {
    const initialPeriod = selected ? detectDayPeriod(selected.date) : null;
    const initialPeriodData = initialPeriod
      ? dataSortedByDateByDayPeriod[currentDateIdx]?.[initialPeriod]
      : null;

    if (initialPeriod && initialPeriodData && initialPeriodData.length > 0) {
      setCurrentDayPeriod(initialPeriod);
    } else {
      const dayPeriodWithData = Object.values(DayPeriodNames).find((period) => {
        const data = dataSortedByDateByDayPeriod[currentDateIdx]?.[period];
        return data && data.length > 0;
      });

      if (dayPeriodWithData) setCurrentDayPeriod(dayPeriodWithData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayedTimeSlots = useMemo(() => {
    return dataSortedByDateByDayPeriod[currentDateIdx]?.[currentDayPeriod];
  }, [dataSortedByDateByDayPeriod, currentDateIdx, currentDayPeriod]);

  return (
    <div {...props} className={cn(stl.block)}>
      <ul className={stl.days}>
        {dataSortedByDateByDayPeriod.map(
          ({ dateDayStr, dateMonthStr }, idx) => (
            <li
              className={cn(stl.day, {
                [stl.selectedDay]: idx === currentDateIdx,
              })}
              key={dateDayStr + dateMonthStr}
              tabIndex={0}
              onClick={() => setCurrentDateIdx(idx)}
              onKeyPress={({ key }) => {
                if (key === " " || key === "Enter") {
                  setCurrentDateIdx(idx);
                }
              }}
            >
              <span>{dateDayStr}</span>
              <i>{dateMonthStr}</i>
            </li>
          )
        )}
      </ul>
      <Tabs
        style={{ width: "100%" }}
        value={currentDayPeriod}
        onChange={setCurrentDayPeriod as any}
      >
        {Object.values(DayPeriodNames).map((period) => {
          const data = dataSortedByDateByDayPeriod[currentDateIdx]?.[period];
          const disabled = data ? !(data.length > 0) : true;

          return (
            <Tabs.TabPane key={period} name={period} disabled={disabled} />
          );
        })}
      </Tabs>
      <ul className={stl.timeSlots}>
        {displayedTimeSlots?.map((item) => {
          return (
            <TimeSlot
              key={item.timeStr}
              item={item}
              selected={
                selected ? isSameMinute(selected.date, item.date) : false
              }
              onSelect={onSelect}
            />
          );
        })}
      </ul>
    </div>
  );
};

export default AvailabilityCard;

const TimeSlot: FC<{
  item: Value;
  selected: boolean;
  onSelect: (v: Value) => void;
}> = ({ item, onSelect, selected }) => {
  const { timeStr, totalPrice } = item;

  return (
    <li
      className={cn(stl.timeSlot, { [stl.selectedTimeSlot]: selected })}
      tabIndex={0}
      onClick={() => onSelect?.(item)}
      onKeyPress={({ key }) => {
        if (key === " " || key === "Enter") {
          onSelect?.(item);
        }
      }}
    >
      <h4>{timeStr}</h4>
      <i>{totalPrice.toFormat()}</i>
    </li>
  );
};
